// SEQ-180 follow-up (14 September 2026): the P&L analytics page's motion, in
// one place, with no React in it so it can be proven under Deno.
//
// WHY A MODULE AND NOT INLINE PROPS. Motion that is typed inline drifts: one
// card springs, the next eases, a third forgets reduced motion entirely. The
// numbers below are the page's whole vocabulary, and pnlMotion.test.ts pins
// the two rules that actually matter: every animated property is a transform
// or an opacity, and reduced motion removes movement rather than shortening
// it.
//
// IT EXTENDS THE APP, IT DOES NOT REPLACE IT. Two places already define how
// this product moves, and both were read before a number below was chosen:
//
//   src/lib/motion.ts        EASE_OUT_EXPO [0.16, 1, 0.3, 1], and a
//                            hidden/visible stagger container.
//   DashboardFilterBar.css   the `dfb-` vocabulary, which is the richest
//                            motion in the app today.
//
// What `dfb-` establishes, and what this module matches:
//
//   dfb-section-enter   0.45s, cubic-bezier(0.16, 1, 0.3, 1), opacity 0 to 1
//                       with translateY(12px) to 0, staggered 60ms per child.
//                       The widget entrance below MATCHES ITS TRAVEL AND ITS
//                       STEP, 12px and 60ms, and deliberately differs in one
//                       way: it settles on a spring rather than that tween, so
//                       a card interrupted mid-arrival by a resize or a
//                       reorder resolves from where it is instead of
//                       restarting a fixed clock. Saying it "is"
//                       dfb-section-enter would be the kind of claim this
//                       module exists to stop making.
//   dfb-overlay-out     0.25s on cubic-bezier(0.4, 0, 1, 1), the app's exit
//                       curve. Things leave faster than they arrive and they
//                       accelerate out. PNL_EXIT_TWEEN is that curve.
//   dfb-count-pop       0.35s on cubic-bezier(0.34, 1.56, 0.64, 1), the
//                       overshoot the app uses when a NUMBER changes.
//                       PNL_COUNT_POP is that curve, applied to the KPI value
//                       when a filter changes it.
//   micro transitions   0.15s to 0.2s, which is --transition-fast.
//
// What this adds, because `dfb-` has no equivalent: spring physics for layout.
// A dashboard whose widgets can be reordered and resized needs position to
// settle, not to ease to a stop on a fixed clock.

/**
 * The app's own easing curve, from src/lib/motion.ts.
 *
 * COPIED, NOT IMPORTED, AND THE COPY IS PROVEN. Importing src/lib/motion.ts
 * pulls framer-motion's types in behind it, and that module's existing
 * `Variants` declarations do not type-check under Deno's checker (they pass
 * under the app's tsc). That would make this module untestable for the sake of
 * four numbers. pnlMotion.test.ts reads src/lib/motion.ts and fails if the two
 * values ever differ, which is the property the import was there for.
 */
export const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

/**
 * The transition shapes this page uses. Declared structurally rather than
 * imported from framer-motion so this module stays free of the animation
 * library, which is what lets a Deno test check the numbers without resolving
 * a browser package.
 */
export type PnlSpring = { type: "spring"; stiffness: number; damping: number };

export type PnlTween = { duration: number; ease: readonly [number, number, number, number] };

/**
 * Widgets arriving. Deliberately soft (a low stiffness against a high damping
 * settles without overshoot), because eight cards bouncing into place on a
 * page the managing director reads would be a toy, not a dashboard.
 */
export const PNL_ENTRANCE_SPRING: PnlSpring = { type: "spring", stiffness: 100, damping: 20 };

/**
 * Reordering and resizing. Stiffer than the entrance: a card the user just
 * dragged should arrive under their hand quickly, and a size change should
 * look like a decision rather than a drift.
 */
export const PNL_LAYOUT_SPRING: PnlSpring = { type: "spring", stiffness: 260, damping: 30 };

/** Hover and press feedback, short enough to feel like a response rather than an animation. */
export const PNL_TACTILE_TWEEN: PnlTween = { duration: 0.18, ease: EASE_OUT_EXPO };

/**
 * Leaving. The app's own exit curve and duration, from `dfb-overlay-out` and
 * `dfb-modal-out`: things go faster than they arrive, and they accelerate as
 * they go, which is the opposite shape to the entrance on purpose.
 */
export const PNL_EXIT_TWEEN: PnlTween = { duration: 0.2, ease: [0.4, 0, 1, 1] };

/**
 * A number changing. `dfb-count-pop` is the app's existing answer for exactly
 * this (a filter changes, a count updates, the badge pops), and its curve
 * overshoots: 1.56 on the second control point is the bounce. Reused here so a
 * KPI reacting to a filter change reads like the rest of the product rather
 * than like a new idea.
 */
export const PNL_COUNT_POP: PnlTween = { duration: 0.35, ease: [0.34, 1.56, 0.64, 1] };

/** The scale keyframes of that pop. Transform only, and it settles back at 1. */
export const PNL_COUNT_POP_SCALE: readonly number[] = [1, 1.06, 1];

/** The gap between one widget appearing and the next. */
export const PNL_STAGGER_SECONDS = 0.06;

/**
 * The longest the whole entrance may take to finish dealing cards out.
 *
 * A dashboard can hold forty widgets. At a flat 60ms each the last one would
 * arrive 2.4 seconds after the first, which stops reading as choreography and
 * starts reading as a slow page. Past this budget the stagger compresses
 * instead of the page getting slower.
 */
export const PNL_MAX_STAGGER_SECONDS = 0.42;

/** The per-child delay for a grid of `childCount` widgets, inside the budget above. */
export function staggerFor(childCount: number): number {
  if (childCount <= 1) return 0;
  return Math.min(PNL_STAGGER_SECONDS, PNL_MAX_STAGGER_SECONDS / childCount);
}

/**
 * One named state of an element. Only the four properties this page is allowed
 * to animate are declarable, which is the type system carrying the same rule
 * ANIMATABLE_PROPERTIES states and the test enforces.
 */
export type PnlVariantTarget = {
  opacity?: number;
  x?: number;
  y?: number;
  scale?: number;
  transition?: PnlSpring | PnlTween | { staggerChildren: number; delayChildren: number };
};

/**
 * Declared as a type alias, not an interface, on purpose: TypeScript gives an
 * alias an implicit index signature, which is what makes these objects
 * assignable to framer-motion's own `Variants` at the call site without this
 * module importing framer-motion and without a cast.
 */
export type PnlVariants = {
  hidden: PnlVariantTarget;
  visible: PnlVariantTarget;
};

/**
 * The grid itself. Holds only the timing: it has no visual state of its own,
 * so nothing about it can flash.
 */
export function gridContainerVariants(reduce: boolean, childCount: number): PnlVariants {
  return {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: reduce ? 0 : staggerFor(childCount),
        delayChildren: reduce ? 0 : 0.04,
      },
    },
  };
}

/**
 * One widget arriving: up and in.
 *
 * Under reduced motion the card still FADES. Removing the fade as well would
 * make a loaded dashboard appear as an eight-card flash, which is harder to
 * read than a quiet cross-fade and is not what the preference asks for. What
 * the preference asks for is no movement, so `y` is zero.
 */
export function widgetItemVariants(reduce: boolean): PnlVariants {
  return {
    // 12px, which is dfb-section-enter's translateY to the pixel.
    hidden: { opacity: 0, y: reduce ? 0 : 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0.2, ease: EASE_OUT_EXPO } : PNL_ENTRANCE_SPRING,
    },
  };
}

/** The tooltip's own entrance, keyed per bucket so moving between bars re-plays it. */
export function tooltipVariants(reduce: boolean): PnlVariants {
  return {
    hidden: { opacity: 0, scale: reduce ? 1 : 0.96, y: reduce ? 0 : 4 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: reduce ? { duration: 0.12, ease: EASE_OUT_EXPO } : { duration: 0.16, ease: EASE_OUT_EXPO },
    },
  };
}

export interface PnlChartAnimation {
  isAnimationActive: boolean;
  animationDuration: number;
  animationBegin: number;
  animationEasing: "ease-out";
}

/**
 * How a Recharts series draws itself in. Recharts owns this animation (it
 * interpolates the path and the bar heights internally), so the page's job is
 * only to say how long, when, and whether at all.
 *
 * Series are offset so a chart with a metric, a secondary and a comparison
 * reads as three arrivals rather than one blur.
 */
export function chartAnimation(reduce: boolean, seriesIndex = 0): PnlChartAnimation {
  return {
    isAnimationActive: !reduce,
    animationDuration: reduce ? 0 : 650,
    animationBegin: reduce ? 0 : seriesIndex * 90,
    animationEasing: "ease-out",
  };
}

/**
 * How long a KPI counts up to its value. Zero under reduced motion, where the
 * number simply IS its value on first paint.
 */
export function countUpSeconds(reduce: boolean): number {
  return reduce ? 0 : 0.9;
}

/**
 * How long a KPI takes to count to a value it is being given for the SECOND
 * time, which in practice means a filter changed.
 *
 * Shorter than the first count on purpose. The first is part of the page
 * arriving and can afford to be slow; a filter change is a response to
 * something the reader just did, and running the full count again churned
 * every card on the page for a second. It is exactly `PNL_COUNT_POP.duration`
 * so the count and the pop that accompanies it are one gesture.
 */
export function recountSeconds(reduce: boolean): number {
  return reduce ? 0 : PNL_COUNT_POP.duration;
}

/**
 * The properties this page is allowed to animate.
 *
 * Anything outside this set (width, height, top, left) is laid out by the
 * browser on the main thread and will stutter a page holding eight charts.
 * pnlMotion.test.ts walks every variant above and fails on a stray key, which
 * is cheaper than finding it in a profiler later.
 */
export const ANIMATABLE_PROPERTIES: readonly string[] = ["opacity", "x", "y", "scale", "transition"];
